import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Stack,
    Text,
    TextField,
    PrimaryButton,
    DefaultButton,
    IconButton,
    MessageBar,
    MessageBarType,
    Spinner,
    SpinnerSize,
    IStackTokens,
    mergeStyleSets,
    FontWeights,
    Dropdown,
    IDropdownOption,
    Toggle,
    DetailsList,
    DetailsListLayoutMode,
    SelectionMode,
    IColumn,
    Selection,
    TooltipHost,
    DirectionalHint,
    CommandBar,
    ICommandBarItemProps,
    Label,
    Separator,
    ProgressIndicator,
    Pivot,
    PivotItem
} from '@fluentui/react';
import {
    TableRegular,
    BrainCircuitRegular,
    ArrowSyncRegular,
    CheckmarkCircleRegular,
    InfoRegular,
    SaveRegular,
    EditRegular,
    ErrorCircleRegular,
    WarningRegular,
    CheckmarkRegular,
    FilterRegular,
    SearchRegular,
    DismissRegular,
    SparkleRegular
} from '@fluentui/react-icons';

// Column analysis status types
type ColumnStatus = 'complete' | 'needs-review' | 'missing';

interface ColumnStatusInfo {
    status: ColumnStatus;
    score: number; // 0-100 completeness score
    missingFields: string[];
    lowConfidenceFields: string[];
    message: string;
}
import { authenticatedFetch } from '../../utils/authApi';
import { TagInput } from '../common/TagInput';

// Types
interface TableReference {
    name: string;
    schema?: string;
    table_type?: string;
    row_count?: number;
    sheet_name?: string;
    active_for_queries: boolean;
    has_mappings?: boolean;
    has_example_questions?: boolean;
    description?: string;
}

interface ColumnDefinition {
    name: string;
    data_type: string;
    is_nullable: boolean;
    is_primary_key?: boolean;
    is_foreign_key?: boolean;
}

interface ColumnMapping {
    column_name: string;
    business_name: string;
    description: string;  // maps to business_description
    category: string;     // maps to semantic_category
    business_role: string;
    format_hint?: string;  // maps to display_format
    aggregation_type?: string;  // maps to default_aggregation
    synonyms: string[];
    synonyms_he?: string[];
    sample_values: string[];
    admin_verified: boolean;  // maps to is_approved
    confidence?: number;
    
    // Display & Formatting
    decimal_places?: number;
    currency_code?: string;
    unit_of_measure?: string;
    
    // Value Intelligence
    expected_range_min?: number;
    expected_range_max?: number;
    
    // Time Intelligence
    is_time_column?: boolean;
    time_granularity?: string;
    supports_yoy?: boolean;
    supports_comparison?: boolean;
    
    // Query Behavior
    sort_direction?: string;
    group_by_priority?: number;
    
    // Relationships
    is_derived_metric?: boolean;
    
    // KPI & Calculation Fields (CRITICAL for AI)
    kpi_formula?: string;           // e.g., "profit = revenue - cost"
    kpi_dependencies?: string[];    // Columns used in formula
    calculation_notes?: string;     // Business rules for calculations
    
    // Filter & Null Handling
    filter_suggestions?: string[];  // Common filter values
    null_handling?: string;         // 'skip' | 'zero' | 'average' | 'default'
}

interface Props {
    sourceId: string;
    onSave?: () => void;
}

const stackTokens: IStackTokens = { childrenGap: 12 };

// Category options (maps to SemanticCategory)
const categoryOptions: IDropdownOption[] = [
    { key: 'identifier', text: 'Identifier' },
    { key: 'financial', text: 'Financial' },
    { key: 'quantity', text: 'Quantity' },
    { key: 'percentage', text: 'Percentage' },
    { key: 'date', text: 'Date' },
    { key: 'datetime', text: 'DateTime' },
    { key: 'text', text: 'Text' },
    { key: 'boolean', text: 'Boolean' },
    { key: 'location', text: 'Location' },
    { key: 'product', text: 'Product' },
    { key: 'customer', text: 'Customer' },
    { key: 'employee', text: 'Employee' },
    { key: 'category', text: 'Category' },
    { key: 'status', text: 'Status' },
    { key: 'calculated', text: 'Calculated' },
    { key: 'unknown', text: 'Unknown' }
];

// Business role options (maps to BusinessRole)
const businessRoleOptions: IDropdownOption[] = [
    { key: 'primary_key', text: 'Primary Key' },
    { key: 'foreign_key', text: 'Foreign Key' },
    { key: 'metric', text: 'Metric (aggregatable)' },
    { key: 'dimension', text: 'Dimension (group by)' },
    { key: 'attribute', text: 'Attribute' },
    { key: 'timestamp', text: 'Timestamp' },
    { key: 'filter', text: 'Filter' },
    { key: 'label', text: 'Label' }
];

// Aggregation type options
const aggregationOptions: IDropdownOption[] = [
    { key: '', text: 'None' },
    { key: 'sum', text: 'Sum' },
    { key: 'average', text: 'Average' },
    { key: 'count', text: 'Count' },
    { key: 'min', text: 'Minimum' },
    { key: 'max', text: 'Maximum' },
    { key: 'count_distinct', text: 'Count Distinct' }
];

// Styles
const styles = mergeStyleSets({
    tableListContainer: {
        border: '1px solid #e1e1e1',
        borderRadius: '8px',
        backgroundColor: 'white',
        maxHeight: '320px',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column' as const
    },
    tableListHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '12px 16px',
        borderBottom: '1px solid #e1e1e1',
        backgroundColor: '#fafafa',
        flexWrap: 'wrap' as const
    },
    tableListBody: {
        overflowY: 'auto' as const,
        flex: 1
    },
    tableListRow: {
        display: 'flex',
        alignItems: 'center',
        padding: '8px 16px',
        borderBottom: '1px solid #f0f0f0',
        cursor: 'pointer',
        transition: 'all 0.15s ease',
        gap: '12px',
        ':hover': {
            backgroundColor: '#f5f5f5'
        }
    },
    tableListRowSelected: {
        backgroundColor: '#e8eeff',
        borderLeft: '3px solid #667eea'
    },
    tableListRowInactive: {
        opacity: 0.6
    },
    tableTypeTag: {
        fontSize: '10px',
        fontWeight: 600,
        padding: '2px 6px',
        borderRadius: '4px',
        textTransform: 'uppercase' as const
    },
    tableTypeTable: {
        backgroundColor: '#e3f2fd',
        color: '#1565c0'
    },
    tableTypeView: {
        backgroundColor: '#fce4ec',
        color: '#c62828'
    },
    tableSectionHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '10px 16px',
        backgroundColor: '#f5f5f5',
        borderRadius: '8px',
        cursor: 'pointer',
        marginBottom: '8px',
        transition: 'all 0.2s ease',
        ':hover': {
            backgroundColor: '#ebebeb'
        }
    },
    tableSectionHeaderIcon: {
        transition: 'transform 0.2s ease'
    },
    tableSectionContent: {
        overflow: 'hidden',
        transition: 'max-height 0.3s ease-out'
    },
    tableSelector: {
        display: 'flex',
        gap: '12px',
        flexWrap: 'wrap',
        marginBottom: '16px'
    },
    tableChip: {
        padding: '8px 16px',
        borderRadius: '20px',
        border: '1px solid #e1e1e1',
        backgroundColor: 'white',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        ':hover': {
            backgroundColor: '#f5f5f5',
            borderColor: '#667eea'
        }
    },
    tableChipSelected: {
        backgroundColor: '#667eea',
        borderColor: '#667eea',
        color: 'white'
    },
    tableChipInactive: {
        backgroundColor: '#f9f9f9',
        borderColor: '#d1d1d1',
        borderStyle: 'dashed'
    },
    mappingCard: {
        padding: '16px',
        backgroundColor: 'white',
        border: '1px solid #e1e1e1',
        borderRadius: '8px',
        marginBottom: '12px'
    },
    columnHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '12px'
    },
    columnName: {
        fontWeight: FontWeights.semibold,
        fontSize: '14px'
    },
    dataType: {
        color: '#666',
        fontSize: '12px',
        backgroundColor: '#f5f5f5',
        padding: '2px 8px',
        borderRadius: '4px'
    },
    fieldRow: {
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: '12px',
        marginBottom: '12px'
    },
    aiSuggestionBadge: {
        backgroundColor: '#e7f3ff',
        color: '#0078d4',
        padding: '2px 6px',
        borderRadius: '4px',
        fontSize: '10px',
        marginLeft: '8px'
    },
    verifiedBadge: {
        backgroundColor: '#d4edda',
        color: '#155724',
        padding: '2px 6px',
        borderRadius: '4px',
        fontSize: '10px',
        marginLeft: '8px'
    },
    sampleValues: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '4px',
        marginTop: '8px'
    },
    sampleValue: {
        backgroundColor: '#f0f0f0',
        padding: '2px 6px',
        borderRadius: '4px',
        fontSize: '11px',
        color: '#666'
    },
    // Status-related styles
    summaryPanel: {
        padding: '16px',
        backgroundColor: '#f8f9fa',
        borderRadius: '8px',
        border: '1px solid #e1e1e1',
        marginBottom: '16px'
    },
    statusBadge: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        padding: '4px 10px',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: 600
    },
    statusComplete: {
        backgroundColor: '#d4edda',
        color: '#155724',
        border: '1px solid #c3e6cb'
    },
    statusNeedsReview: {
        backgroundColor: '#fff3cd',
        color: '#856404',
        border: '1px solid #ffc107'
    },
    statusMissing: {
        backgroundColor: '#f8d7da',
        color: '#721c24',
        border: '1px solid #f5c6cb'
    },
    statusCount: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '8px 16px',
        borderRadius: '8px',
        cursor: 'pointer',
        transition: 'all 0.2s ease'
    },
    statusCountComplete: {
        backgroundColor: '#d4edda',
        border: '1px solid #c3e6cb'
    },
    statusCountNeedsReview: {
        backgroundColor: '#fff3cd',
        border: '1px solid #ffc107'
    },
    statusCountMissing: {
        backgroundColor: '#f8d7da',
        border: '1px solid #f5c6cb'
    },
    cardStatusBorder: {
        borderLeftWidth: '4px',
        borderLeftStyle: 'solid'
    },
    cardComplete: {
        borderLeftColor: '#28a745'
    },
    cardNeedsReview: {
        borderLeftColor: '#ffc107'
    },
    cardMissing: {
        borderLeftColor: '#dc3545'
    },
    focusAlert: {
        padding: '12px 16px',
        backgroundColor: '#fff3cd',
        border: '1px solid #ffc107',
        borderRadius: '8px',
        marginBottom: '16px'
    },
    // Modern AI Analyze Button
    aiButtonContainer: {
        position: 'relative' as const,
        display: 'inline-block'
    },
    aiButton: {
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        border: 'none',
        borderRadius: '12px',
        color: 'white',
        padding: '12px 24px',
        fontSize: '14px',
        fontWeight: 600,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        transition: 'all 0.3s ease',
        boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
        overflow: 'hidden',
        position: 'relative' as const,
        ':hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 6px 20px rgba(102, 126, 234, 0.5)'
        }
    },
    aiButtonDisabled: {
        opacity: 0.7,
        cursor: 'not-allowed'
    },
    aiButtonShimmer: {
        position: 'absolute' as const,
        top: 0,
        left: '-100%',
        width: '100%',
        height: '100%',
        background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
        animation: 'shimmer 2s infinite'
    },
    aiButtonProgress: {
        position: 'absolute' as const,
        bottom: 0,
        left: 0,
        height: '3px',
        background: 'rgba(255,255,255,0.8)',
        borderRadius: '0 0 12px 12px',
        animation: 'progressBounce 1.5s ease-in-out infinite'
    },
    searchContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        backgroundColor: 'white',
        border: '1px solid #e1e1e1',
        borderRadius: '8px',
        padding: '4px 12px',
        minWidth: '280px',
        transition: 'all 0.2s ease',
        ':focus-within': {
            borderColor: '#667eea',
            boxShadow: '0 0 0 2px rgba(102, 126, 234, 0.2)'
        }
    },
    searchInput: {
        border: 'none',
        outline: 'none',
        fontSize: '14px',
        flex: 1,
        padding: '8px 0',
        backgroundColor: 'transparent'
    },
    searchClear: {
        cursor: 'pointer',
        color: '#999',
        ':hover': {
            color: '#666'
        }
    }
});

// Transform backend response to frontend ColumnMapping format
const transformBackendMapping = (m: any): ColumnMapping => {
    return {
        column_name: m.column_name,
        business_name: m.business_name || '',
        description: m.business_description || m.description || '',  // Backend uses business_description
        category: m.semantic_category || m.category || 'unknown',    // Backend uses semantic_category
        business_role: m.business_role || 'attribute',
        format_hint: m.display_format || m.format_hint || '',        // Backend uses display_format
        aggregation_type: m.default_aggregation || m.aggregation_type || '',
        synonyms: m.synonyms || [],
        synonyms_he: m.synonyms_he || [],
        sample_values: m.sample_values || [],
        admin_verified: m.is_approved || m.admin_verified || false,  // Backend uses is_approved
        confidence: m.confidence || 0,
        
        // Display & Formatting
        decimal_places: m.decimal_places,
        currency_code: m.currency_code,
        unit_of_measure: m.unit_of_measure,
        
        // Value Intelligence
        expected_range_min: m.expected_range_min,
        expected_range_max: m.expected_range_max,
        
        // Time Intelligence  
        is_time_column: m.is_time_column || false,
        time_granularity: m.time_granularity,
        supports_yoy: m.supports_yoy || false,
        supports_comparison: m.supports_comparison || false,
        
        // Query Behavior
        sort_direction: m.sort_direction,
        group_by_priority: m.group_by_priority,
        
        // Relationships
        is_derived_metric: m.is_derived_metric || false,
        
        // KPI & Calculation Fields
        kpi_formula: m.kpi_formula || '',
        kpi_dependencies: m.kpi_dependencies || [],
        calculation_notes: m.calculation_notes || '',
        
        // Filter & Null Handling
        filter_suggestions: m.filter_suggestions || [],
        null_handling: m.null_handling || ''
    };
};

// Calculate column analysis status based on mapping completeness
const calculateColumnStatus = (mapping: ColumnMapping): ColumnStatusInfo => {
    const missingFields: string[] = [];
    const lowConfidenceFields: string[] = [];
    let score = 0;
    const maxScore = 100;
    
    // Core required fields (60% weight)
    const coreFields = [
        { name: 'Business Name', value: mapping.business_name, weight: 20 },
        { name: 'Category', value: mapping.category !== 'unknown' ? mapping.category : '', weight: 15 },
        { name: 'Business Role', value: mapping.business_role !== 'attribute' ? mapping.business_role : '', weight: 15 },
        { name: 'Description', value: mapping.description, weight: 10 }
    ];
    
    for (const field of coreFields) {
        if (field.value && field.value.trim && field.value.trim() !== '') {
            score += field.weight;
        } else if (field.value) {
            score += field.weight;
        } else {
            missingFields.push(field.name);
        }
    }
    
    // Important fields (25% weight)
    const importantFields = [
        { name: 'Synonyms', value: mapping.synonyms && mapping.synonyms.length > 0, weight: 10 },
        { name: 'Aggregation Type', value: mapping.aggregation_type, weight: 8 },
        { name: 'Display Format', value: mapping.format_hint, weight: 7 }
    ];
    
    for (const field of importantFields) {
        if (field.value) {
            score += field.weight;
        } else {
            missingFields.push(field.name);
        }
    }
    
    // Optional fields (15% weight) - just add to score, don't mark as missing
    if (mapping.unit_of_measure) score += 5;
    if (mapping.expected_range_min !== undefined || mapping.expected_range_max !== undefined) score += 5;
    if (mapping.is_time_column && mapping.time_granularity) score += 5;
    
    // Check for low confidence
    if (mapping.confidence !== undefined && mapping.confidence > 0 && mapping.confidence < 0.6) {
        lowConfidenceFields.push(`AI Confidence: ${(mapping.confidence * 100).toFixed(0)}%`);
    }
    
    // Determine status
    let status: ColumnStatus;
    let message: string;
    
    if (mapping.admin_verified) {
        status = 'complete';
        message = 'Verified by admin';
    } else if (score >= 70) {
        if (lowConfidenceFields.length > 0) {
            status = 'needs-review';
            message = `Good coverage but low AI confidence`;
        } else {
            status = 'complete';
            message = 'AI analysis complete';
        }
    } else if (score >= 35) {
        status = 'needs-review';
        const missing = missingFields.slice(0, 3).join(', ');
        message = `Missing: ${missing}`;
    } else {
        status = 'missing';
        message = !mapping.business_name ? 'Needs AI analysis' : `Missing ${missingFields.length} key fields`;
    }
    
    return { status, score, missingFields, lowConfidenceFields, message };
};

// Status display helper
const getStatusIcon = (status: ColumnStatus) => {
    switch (status) {
        case 'complete':
            return <CheckmarkCircleRegular style={{ color: '#28a745', fontSize: '18px' }} />;
        case 'needs-review':
            return <WarningRegular style={{ color: '#ffc107', fontSize: '18px' }} />;
        case 'missing':
            return <ErrorCircleRegular style={{ color: '#dc3545', fontSize: '18px' }} />;
    }
};

const getStatusLabel = (status: ColumnStatus) => {
    switch (status) {
        case 'complete': return 'Complete';
        case 'needs-review': return 'Needs Review';
        case 'missing': return 'Missing';
    }
};

// Inject keyframe animations
const injectAnimations = () => {
    const styleId = 'semantic-mapping-animations';
    if (!document.getElementById(styleId)) {
        const style = document.createElement('style');
        style.id = styleId;
        style.textContent = `
            @keyframes shimmer {
                0% { left: -100%; }
                100% { left: 100%; }
            }
            @keyframes progressBounce {
                0%, 100% { width: 20%; left: 0; }
                25% { width: 40%; left: 10%; }
                50% { width: 30%; left: 50%; }
                75% { width: 50%; left: 30%; }
            }
            @keyframes pulse {
                0%, 100% { opacity: 1; }
                50% { opacity: 0.7; }
            }
            @keyframes sparkle {
                0%, 100% { transform: scale(1) rotate(0deg); opacity: 1; }
                50% { transform: scale(1.2) rotate(180deg); opacity: 0.8; }
            }
            @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
            }
            .sync-spinning i {
                animation: spin 1s linear infinite;
            }
        `;
        document.head.appendChild(style);
    }
};

export const SemanticMappingEditor: React.FC<Props> = ({ sourceId, onSave }) => {
    // Inject animations on mount
    useEffect(() => {
        injectAnimations();
    }, []);
    
    // State
    const [tables, setTables] = useState<TableReference[]>([]);
    const [selectedTable, setSelectedTable] = useState<string | null>(null);
    const [columns, setColumns] = useState<ColumnDefinition[]>([]);
    const [mappings, setMappings] = useState<Record<string, ColumnMapping>>({});
    const [loading, setLoading] = useState(false);
    const [analyzing, setAnalyzing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [expandedColumn, setExpandedColumn] = useState<string | null>(null);
    const [statusFilter, setStatusFilter] = useState<ColumnStatus | 'all'>('all');
    const [hasRunAnalysis, setHasRunAnalysis] = useState(false);
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [analyzeProgress, setAnalyzeProgress] = useState<number>(0);
    const [tableSearchQuery, setTableSearchQuery] = useState<string>('');
    const [tableTypeFilter, setTableTypeFilter] = useState<'active' | 'all' | 'table' | 'view'>('active');
    const [syncing, setSyncing] = useState(false);
    const [syncStatus, setSyncStatus] = useState<string | null>(null);
    const [tableDescription, setTableDescription] = useState<string>('');
    const [savingDescription, setSavingDescription] = useState(false);
    const [enhancingDescription, setEnhancingDescription] = useState(false);
    
    // Filter and organize tables/views
    const filteredTables = useMemo(() => {
        return tables.filter(t => {
            // Active filter (default)
            if (tableTypeFilter === 'active' && !t.active_for_queries) return false;
            // Type filter
            if (tableTypeFilter === 'table' && t.table_type === 'view') return false;
            if (tableTypeFilter === 'view' && t.table_type !== 'view') return false;
            // Search filter
            if (tableSearchQuery && !t.name.toLowerCase().includes(tableSearchQuery.toLowerCase())) return false;
            return true;
        });
    }, [tables, tableTypeFilter, tableSearchQuery]);
    
    // Count by type
    const tableCounts = useMemo(() => ({
        tables: tables.filter(t => t.table_type !== 'view').length,
        views: tables.filter(t => t.table_type === 'view').length,
        activeCount: tables.filter(t => t.active_for_queries).length
    }), [tables]);
    
    // Calculate status for all columns and create summary
    const columnStatuses = useMemo(() => {
        const statuses: Record<string, ColumnStatusInfo> = {};
        for (const col of columns) {
            const mapping = mappings[col.name];
            if (mapping) {
                statuses[col.name] = calculateColumnStatus(mapping);
            }
        }
        return statuses;
    }, [columns, mappings]);
    
    // Summary statistics
    const statusSummary = useMemo(() => {
        const summary = { complete: 0, 'needs-review': 0, missing: 0, total: columns.length };
        for (const col of columns) {
            const status = columnStatuses[col.name]?.status || 'missing';
            summary[status]++;
        }
        return summary;
    }, [columns, columnStatuses]);
    
    // Filter and sort columns by status (Missing first, then Needs Review, then Complete)
    const sortedColumns = useMemo(() => {
        const statusOrder: Record<ColumnStatus, number> = { 'missing': 0, 'needs-review': 1, 'complete': 2 };
        const searchLower = searchQuery.toLowerCase().trim();
        
        let filtered = columns;
        
        // Apply search filter first
        if (searchLower) {
            filtered = columns.filter(col => {
                const mapping = mappings[col.name];
                return (
                    col.name.toLowerCase().includes(searchLower) ||
                    mapping?.business_name?.toLowerCase().includes(searchLower) ||
                    mapping?.description?.toLowerCase().includes(searchLower) ||
                    mapping?.synonyms?.some(s => s.toLowerCase().includes(searchLower))
                );
            });
        }
        
        // Then apply status filter
        if (statusFilter !== 'all') {
            filtered = filtered.filter(col => columnStatuses[col.name]?.status === statusFilter);
        }
        
        return [...filtered].sort((a, b) => {
            const statusA = columnStatuses[a.name]?.status || 'missing';
            const statusB = columnStatuses[b.name]?.status || 'missing';
            return statusOrder[statusA] - statusOrder[statusB];
        });
    }, [columns, columnStatuses, statusFilter, searchQuery, mappings]);
    
    // Fetch tables
    const fetchTables = useCallback(async () => {
        setLoading(true);
        try {
            const response = await authenticatedFetch(`/api/admin/structured-sources/${sourceId}/tables`);
            if (response.ok) {
                const data = await response.json();
                setTables(data.tables || []);
            } else {
                setError('Failed to load tables');
            }
        } catch (err) {
            setError('Error loading tables');
        } finally {
            setLoading(false);
        }
    }, [sourceId]);
    
    // Toggle table active for queries
    const handleToggleTableActive = useCallback(async (tableName: string, currentActive: boolean, schema?: string) => {
        const newActive = !currentActive;
        try {
            const response = await authenticatedFetch(
                `/api/admin/structured-sources/${sourceId}/tables/${encodeURIComponent(tableName)}/toggle-active`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ active: newActive, schema })
                }
            );
            
            if (response.ok) {
                // Update local state
                setTables(prev => prev.map(t => 
                    t.name === tableName ? { ...t, active_for_queries: newActive } : t
                ));
                setSuccess(`Table "${tableName}" ${newActive ? 'enabled' : 'disabled'} for AI queries`);
                setTimeout(() => setSuccess(null), 3000);
            } else {
                setError('Failed to update table status');
            }
        } catch (err) {
            setError('Error updating table status');
        }
    }, [sourceId]);
    
    // Sync table metadata from database
    const handleSyncMetadata = useCallback(async () => {
        setSyncing(true);
        setSyncStatus('Connecting to database...');
        try {
            const response = await authenticatedFetch(
                `/api/admin/structured-sources/${sourceId}/sync-metadata`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' }
                }
            );
            
            if (response.ok) {
                const data = await response.json();
                setSyncStatus(`✓ Synced ${data.tables_synced} tables`);
                setSuccess(`Synced ${data.tables_synced} tables with schema metadata`);
                setTimeout(() => {
                    setSuccess(null);
                    setSyncStatus(null);
                }, 3000);
                // Refresh tables list
                fetchTables();
            } else {
                const errorData = await response.json();
                setSyncStatus(null);
                setError(errorData.error || 'Failed to sync metadata');
            }
        } catch (err) {
            setSyncStatus(null);
            setError('Error syncing metadata');
        } finally {
            setSyncing(false);
        }
    }, [sourceId, fetchTables]);
    
    // Fetch table schema
    const fetchTableSchema = useCallback(async (tableName: string) => {
        setLoading(true);
        try {
            const response = await authenticatedFetch(`/api/admin/structured-sources/${sourceId}/schema/${encodeURIComponent(tableName)}`);
            if (response.ok) {
                const data = await response.json();
                setColumns(data.columns || []);
                
                // Initialize empty mappings for columns without mappings
                const newMappings: Record<string, ColumnMapping> = {};
                for (const col of data.columns || []) {
                    newMappings[col.name] = mappings[col.name] || {
                        column_name: col.name,
                        business_name: '',
                        description: '',
                        category: 'unknown',
                        business_role: 'attribute',
                        synonyms: [],
                        sample_values: [],
                        admin_verified: false
                    };
                }
                setMappings(newMappings);
            }
        } catch (err) {
            setError('Error loading schema');
        } finally {
            setLoading(false);
        }
    }, [sourceId, mappings]);
    
    // Load existing mappings
    const loadExistingMappings = useCallback(async (tableName: string) => {
        try {
            const response = await authenticatedFetch(`/api/admin/structured-sources/${sourceId}`);
            if (response.ok) {
                const data = await response.json();
                if (data.semantic_mappings && data.semantic_mappings[tableName]) {
                    const existingMappings: Record<string, ColumnMapping> = {};
                    for (const m of data.semantic_mappings[tableName]) {
                        existingMappings[m.column_name] = transformBackendMapping(m);
                    }
                    setMappings(prev => ({ ...prev, ...existingMappings }));
                }
            }
        } catch (err) {
            console.error('Error loading existing mappings:', err);
        }
    }, [sourceId]);
    
    useEffect(() => {
        fetchTables();
    }, [fetchTables]);
    
    // No auto-select - user must actively click a table/view to see columns
    
    // When table is selected
    const handleSelectTable = async (tableName: string) => {
        setSelectedTable(tableName);
        setExpandedColumn(null);
        setSearchQuery('');
        setLoading(true);
        
        // Find the table object to get its schema and description
        const tableObj = tables.find(t => t.name === tableName);
        const schemaName = tableObj?.schema || 'dbo';
        const schemaParam = schemaName ? `?schema=${encodeURIComponent(schemaName)}` : '';
        
        // Load table description
        setTableDescription(tableObj?.description || '');
        
        try {
            // Load schema and existing mappings in parallel
            const [schemaResponse, sourceResponse] = await Promise.all([
                authenticatedFetch(`/api/admin/structured-sources/${sourceId}/schema/${encodeURIComponent(tableName)}${schemaParam}`),
                authenticatedFetch(`/api/admin/structured-sources/${sourceId}`)
            ]);
            
            let newColumns: ColumnDefinition[] = [];
            let existingMappings: Record<string, ColumnMapping> = {};
            
            // Get columns from schema
            if (schemaResponse.ok) {
                const schemaData = await schemaResponse.json();
                newColumns = schemaData.columns || [];
                setColumns(newColumns);
            }
            
            // Get existing mappings from source
            if (sourceResponse.ok) {
                const sourceData = await sourceResponse.json();
                if (sourceData.semantic_mappings && sourceData.semantic_mappings[tableName]) {
                    for (const m of sourceData.semantic_mappings[tableName]) {
                        existingMappings[m.column_name] = transformBackendMapping(m);
                    }
                }
            }
            
            // Merge: use existing mappings if available, otherwise create empty mapping
            const mergedMappings: Record<string, ColumnMapping> = {};
            for (const col of newColumns) {
                if (existingMappings[col.name]) {
                    mergedMappings[col.name] = existingMappings[col.name];
                } else {
                    mergedMappings[col.name] = {
                        column_name: col.name,
                        business_name: '',
                        description: '',
                        category: 'unknown',
                        business_role: 'attribute',
                        synonyms: [],
                        sample_values: [],
                        admin_verified: false
                    };
                }
            }
            setMappings(mergedMappings);
            
            // Check if we have any pre-existing data to trigger hasRunAnalysis
            if (Object.values(existingMappings).some(m => m.business_name)) {
                setHasRunAnalysis(true);
            }
            
        } catch (err) {
            setError('Error loading table data');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };
    
    // Run AI analysis
    const handleAnalyze = async () => {
        if (!selectedTable) return;
        
        // Get schema from the selected table object
        const tableObj = tables.find(t => t.name === selectedTable);
        const schemaName = tableObj?.schema || 'dbo';
        
        setAnalyzing(true);
        setError(null);
        try {
            const response = await authenticatedFetch(`/api/admin/structured-sources/${sourceId}/analyze`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ 
                    tables: [{ name: selectedTable, schema: schemaName }] 
                })
            });
            
            if (response.ok) {
                const data = await response.json();
                if (data[selectedTable]?.semantic_mappings) {
                    // Merge AI suggestions with existing mappings
                    const aiMappings: Record<string, ColumnMapping> = {};
                    for (const m of data[selectedTable].semantic_mappings) {
                        const transformed = transformBackendMapping(m);
                        transformed.admin_verified = false;  // AI suggestions are not verified
                        aiMappings[m.column_name] = transformed;
                    }
                    setMappings(prev => {
                        const merged: Record<string, ColumnMapping> = {};
                        for (const [colName, existing] of Object.entries(prev)) {
                            if (existing.admin_verified) {
                                // Keep admin-verified mappings
                                merged[colName] = existing;
                            } else if (aiMappings[colName]) {
                                // Use AI suggestion
                                merged[colName] = aiMappings[colName];
                            } else {
                                merged[colName] = existing;
                            }
                        }
                        return merged;
                    });
                    setSuccess('AI analysis complete - review status indicators below');
                    setHasRunAnalysis(true);
                }
            } else {
                setError('Analysis failed');
            }
        } catch (err) {
            setError('Error running analysis');
        } finally {
            setAnalyzing(false);
        }
    };
    
    // Update mapping
    const updateMapping = (columnName: string, field: keyof ColumnMapping, value: any) => {
        setMappings(prev => ({
            ...prev,
            [columnName]: {
                ...prev[columnName],
                [field]: value
            }
        }));
    };
    
    // Save mappings
    const handleSave = async () => {
        if (!selectedTable) {
            console.error('[SemanticMappingEditor] No table selected');
            setError('No table selected');
            return;
        }
        
        setSaving(true);
        setError(null);
        try {
            const mappingsArray = Object.values(mappings);
            console.log('[SemanticMappingEditor] Saving mappings:', { 
                sourceId, 
                table: selectedTable, 
                count: mappingsArray.length 
            });
            
            const url = `/api/admin/structured-sources/${sourceId}/mappings/${encodeURIComponent(selectedTable)}`;
            console.log('[SemanticMappingEditor] PUT URL:', url);
            
            const response = await authenticatedFetch(url, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ mappings: mappingsArray })
            });
            
            console.log('[SemanticMappingEditor] Response status:', response.status);
            
            if (response.ok) {
                const data = await response.json();
                console.log('[SemanticMappingEditor] Save success:', data);
                setSuccess('Mappings saved successfully');
                // Refresh tables list to update has_mappings marker
                fetchTables();
                // Don't call onSave immediately - it causes parent to refetch and reset state
                // onSave?.();
            } else {
                const errorData = await response.json().catch(() => ({}));
                console.error('[SemanticMappingEditor] Save failed:', response.status, errorData);
                setError(`Failed to save mappings: ${errorData.error || response.statusText}`);
            }
        } catch (err) {
            console.error('[SemanticMappingEditor] Save error:', err);
            setError(`Error saving mappings: ${err instanceof Error ? err.message : 'Unknown error'}`);
        } finally {
            setSaving(false);
        }
    };
    
    // Save table description
    const handleSaveDescription = async () => {
        if (!selectedTable) return;
        
        setSavingDescription(true);
        try {
            const response = await authenticatedFetch(
                `/api/admin/structured-sources/${sourceId}/tables/${encodeURIComponent(selectedTable)}/description`,
                {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ description: tableDescription })
                }
            );
            
            if (response.ok) {
                // Update local table state with new description
                setTables(prev => prev.map(t => 
                    t.name === selectedTable ? { ...t, description: tableDescription } : t
                ));
                setSuccess('Table description saved');
            } else {
                const errorData = await response.json().catch(() => ({}));
                setError(`Failed to save description: ${errorData.error || response.statusText}`);
            }
        } catch (err) {
            setError(`Error saving description: ${err instanceof Error ? err.message : 'Unknown error'}`);
        } finally {
            setSavingDescription(false);
        }
    };
    
    // AI enhance table description
    const handleEnhanceDescription = async () => {
        if (!selectedTable) return;
        
        setEnhancingDescription(true);
        try {
            const tableObj = tables.find(t => t.name === selectedTable);
            const response = await authenticatedFetch(
                `/api/admin/structured-sources/${sourceId}/tables/${encodeURIComponent(selectedTable)}/enhance-description`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ 
                        current_description: tableDescription,
                        schema: tableObj?.schema
                    })
                }
            );
            
            if (response.ok) {
                const data = await response.json();
                setTableDescription(data.enhanced_description);
                setSuccess('AI generated description - review and save');
            } else {
                const errorData = await response.json().catch(() => ({}));
                setError(`Failed to enhance description: ${errorData.error || response.statusText}`);
            }
        } catch (err) {
            setError(`Error enhancing description: ${err instanceof Error ? err.message : 'Unknown error'}`);
        } finally {
            setEnhancingDescription(false);
        }
    };
    
    // Mark as verified
    const handleVerify = (columnName: string) => {
        updateMapping(columnName, 'admin_verified', true);
    };
    
    // Mark as unverified
    const handleUnverify = (columnName: string) => {
        updateMapping(columnName, 'admin_verified', false);
    };
    
    // Verify all columns that have a business name (i.e., have been analyzed)
    const handleVerifyAll = () => {
        const updatedMappings: Record<string, ColumnMapping> = {};
        for (const [colName, mapping] of Object.entries(mappings)) {
            if (mapping.business_name) {
                updatedMappings[colName] = { ...mapping, admin_verified: true };
            } else {
                updatedMappings[colName] = mapping;
            }
        }
        setMappings(updatedMappings);
        setSuccess(`Verified ${Object.values(updatedMappings).filter(m => m.admin_verified).length} columns`);
    };
    
    // Unverify all columns
    const handleUnverifyAll = () => {
        const updatedMappings: Record<string, ColumnMapping> = {};
        for (const [colName, mapping] of Object.entries(mappings)) {
            updatedMappings[colName] = { ...mapping, admin_verified: false };
        }
        setMappings(updatedMappings);
        setSuccess('Unverified all columns');
    };
    
    // Render column mapping card
    const renderColumnCard = (col: ColumnDefinition) => {
        const mapping = mappings[col.name] || {
            column_name: col.name,
            business_name: '',
            description: '',
            category: 'unknown',
            business_role: 'attribute',
            synonyms: [],
            sample_values: [],
            admin_verified: false
        };
        
        const isExpanded = expandedColumn === col.name;
        const statusInfo = columnStatuses[col.name] || { status: 'missing' as ColumnStatus, score: 0, missingFields: [], lowConfidenceFields: [], message: '' };
        
        // Get card border style based on status
        const cardStatusClass = statusInfo.status === 'complete' ? styles.cardComplete 
            : statusInfo.status === 'needs-review' ? styles.cardNeedsReview 
            : styles.cardMissing;
        
        return (
            <div key={col.name} className={`${styles.mappingCard} ${styles.cardStatusBorder} ${cardStatusClass}`}>
                <div className={styles.columnHeader}>
                    <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
                        {/* Status Icon */}
                        <TooltipHost
                            content={
                                <Stack tokens={{ childrenGap: 4 }}>
                                    <Text variant="smallPlus" style={{ fontWeight: 600 }}>{getStatusLabel(statusInfo.status)}</Text>
                                    <Text variant="small">Completeness: {statusInfo.score}%</Text>
                                    {statusInfo.missingFields.length > 0 && (
                                        <Text variant="small">Missing: {statusInfo.missingFields.slice(0, 5).join(', ')}</Text>
                                    )}
                                    {statusInfo.lowConfidenceFields.length > 0 && (
                                        <Text variant="small">{statusInfo.lowConfidenceFields[0]}</Text>
                                    )}
                                </Stack>
                            }
                            directionalHint={DirectionalHint.topCenter}
                        >
                            <span style={{ display: 'flex', alignItems: 'center' }}>
                                {getStatusIcon(statusInfo.status)}
                            </span>
                        </TooltipHost>
                        <Text className={styles.columnName}>{col.name}</Text>
                        <span className={styles.dataType}>{col.data_type}</span>
                        {col.is_primary_key && (
                            <span style={{ backgroundColor: '#fff3cd', color: '#856404', padding: '2px 6px', borderRadius: '4px', fontSize: '10px' }}>
                                PK
                            </span>
                        )}
                        {col.is_foreign_key && (
                            <span style={{ backgroundColor: '#d4edda', color: '#155724', padding: '2px 6px', borderRadius: '4px', fontSize: '10px' }}>
                                FK
                            </span>
                        )}
                        {mapping.admin_verified && (
                            <span className={styles.verifiedBadge}>
                                <CheckmarkCircleRegular style={{ fontSize: '10px', marginRight: '4px' }} />
                                Verified
                            </span>
                        )}
                        {!mapping.admin_verified && mapping.business_name && (
                            <span className={styles.aiSuggestionBadge}>
                                <BrainCircuitRegular style={{ fontSize: '10px', marginRight: '4px' }} />
                                AI Suggestion
                            </span>
                        )}
                        {/* Status message */}
                        {hasRunAnalysis && statusInfo.message && (
                            <Text variant="small" style={{ 
                                color: statusInfo.status === 'complete' ? '#155724' : statusInfo.status === 'needs-review' ? '#856404' : '#721c24',
                                fontStyle: 'italic'
                            }}>
                                {statusInfo.message}
                            </Text>
                        )}
                    </Stack>
                    <Stack horizontal tokens={{ childrenGap: 8 }}>
                        {mapping.admin_verified && (
                            <DefaultButton
                                text="Unverify"
                                iconProps={{ iconName: 'Cancel' }}
                                onClick={() => handleUnverify(col.name)}
                                styles={{ root: { minWidth: '90px', backgroundColor: '#fff3cd', border: '1px solid #ffc107' } }}
                            />
                        )}
                        {!mapping.admin_verified && mapping.business_name && (
                            <DefaultButton
                                text="Verify"
                                iconProps={{ iconName: 'CheckMark' }}
                                onClick={() => handleVerify(col.name)}
                                styles={{ root: { minWidth: '80px' } }}
                            />
                        )}
                        <IconButton
                            iconProps={{ iconName: isExpanded ? 'ChevronUp' : 'ChevronDown' }}
                            onClick={() => setExpandedColumn(isExpanded ? null : col.name)}
                        />
                    </Stack>
                </div>
                
                {/* Basic fields always visible */}
                <div className={styles.fieldRow}>
                    <TextField
                        label="Business Name"
                        value={mapping.business_name}
                        onChange={(_, val) => updateMapping(col.name, 'business_name', val || '')}
                        placeholder="e.g., Customer Name"
                    />
                    <Dropdown
                        label="Category"
                        selectedKey={mapping.category || 'unknown'}
                        options={categoryOptions}
                        onChange={(_, option) => updateMapping(col.name, 'category', option?.key || 'unknown')}
                    />
                    <Dropdown
                        label="Business Role"
                        selectedKey={mapping.business_role || 'attribute'}
                        options={businessRoleOptions}
                        onChange={(_, option) => updateMapping(col.name, 'business_role', option?.key || 'attribute')}
                    />
                </div>
                
                {/* Expanded section */}
                {isExpanded && (
                    <Stack tokens={stackTokens} styles={{ root: { marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #f0f0f0' } }}>
                        <TextField
                            label="Description"
                            multiline
                            rows={2}
                            value={mapping.description}
                            onChange={(_, val) => updateMapping(col.name, 'description', val || '')}
                            placeholder="Describe what this column represents in business terms"
                        />
                        
                        {/* Display & Formatting Row */}
                        <div className={styles.fieldRow}>
                            <Dropdown
                                label="Display Format"
                                selectedKey={mapping.format_hint || ''}
                                options={[
                                    { key: '', text: 'None' },
                                    { key: 'currency', text: 'Currency' },
                                    { key: 'percentage', text: 'Percentage' },
                                    { key: 'number', text: 'Number' },
                                    { key: 'integer', text: 'Integer' },
                                    { key: 'date', text: 'Date' },
                                    { key: 'text', text: 'Text' }
                                ]}
                                onChange={(_, option) => updateMapping(col.name, 'format_hint', option?.key as string)}
                            />
                            <TextField
                                label="Currency Code"
                                value={mapping.currency_code || ''}
                                onChange={(_, val) => updateMapping(col.name, 'currency_code', val || '')}
                                placeholder="ILS, USD, EUR"
                            />
                            <TextField
                                label="Unit of Measure"
                                value={mapping.unit_of_measure || ''}
                                onChange={(_, val) => updateMapping(col.name, 'unit_of_measure', val || '')}
                                placeholder="kg, units, hours"
                            />
                        </div>
                        
                        {/* Query Behavior Row */}
                        <div className={styles.fieldRow}>
                            <Dropdown
                                label="Aggregation Type"
                                selectedKey={mapping.aggregation_type || ''}
                                options={aggregationOptions}
                                onChange={(_, option) => updateMapping(col.name, 'aggregation_type', option?.key as string)}
                            />
                            <Dropdown
                                label="Sort Direction"
                                selectedKey={mapping.sort_direction || ''}
                                options={[
                                    { key: '', text: 'None' },
                                    { key: 'asc', text: 'Ascending' },
                                    { key: 'desc', text: 'Descending' }
                                ]}
                                onChange={(_, option) => updateMapping(col.name, 'sort_direction', option?.key as string)}
                            />
                            <TextField
                                label="Group By Priority (1-10)"
                                type="number"
                                value={mapping.group_by_priority?.toString() || ''}
                                onChange={(_, val) => updateMapping(col.name, 'group_by_priority', val ? parseInt(val) : undefined)}
                                placeholder="Higher = more likely"
                            />
                        </div>
                        
                        {/* Time Intelligence Row */}
                        <Stack horizontal tokens={{ childrenGap: 16 }}>
                            <Toggle
                                label="Time Column"
                                checked={mapping.is_time_column || false}
                                onChange={(_, checked) => updateMapping(col.name, 'is_time_column', checked)}
                            />
                            <Dropdown
                                label="Time Granularity"
                                selectedKey={mapping.time_granularity || ''}
                                disabled={!mapping.is_time_column}
                                options={[
                                    { key: '', text: 'None' },
                                    { key: 'year', text: 'Year' },
                                    { key: 'quarter', text: 'Quarter' },
                                    { key: 'month', text: 'Month' },
                                    { key: 'week', text: 'Week' },
                                    { key: 'day', text: 'Day' },
                                    { key: 'hour', text: 'Hour' }
                                ]}
                                onChange={(_, option) => updateMapping(col.name, 'time_granularity', option?.key as string)}
                                styles={{ root: { minWidth: '120px' } }}
                            />
                            <Toggle
                                label="Supports YoY"
                                checked={mapping.supports_yoy || false}
                                disabled={!mapping.is_time_column}
                                onChange={(_, checked) => updateMapping(col.name, 'supports_yoy', checked)}
                            />
                            <Toggle
                                label="Supports Comparison"
                                checked={mapping.supports_comparison || false}
                                onChange={(_, checked) => updateMapping(col.name, 'supports_comparison', checked)}
                            />
                        </Stack>
                        
                        {/* Value Range Row */}
                        <Stack horizontal tokens={{ childrenGap: 16 }}>
                            <TextField
                                label="Expected Min"
                                type="number"
                                value={mapping.expected_range_min?.toString() || ''}
                                onChange={(_, val) => updateMapping(col.name, 'expected_range_min', val ? parseFloat(val) : undefined)}
                                styles={{ root: { width: '120px' } }}
                            />
                            <TextField
                                label="Expected Max"
                                type="number"
                                value={mapping.expected_range_max?.toString() || ''}
                                onChange={(_, val) => updateMapping(col.name, 'expected_range_max', val ? parseFloat(val) : undefined)}
                                styles={{ root: { width: '120px' } }}
                            />
                            <TextField
                                label="Decimal Places"
                                type="number"
                                value={mapping.decimal_places?.toString() || ''}
                                onChange={(_, val) => updateMapping(col.name, 'decimal_places', val ? parseInt(val) : undefined)}
                                styles={{ root: { width: '100px' } }}
                            />
                            <Toggle
                                label="Derived Metric"
                                checked={mapping.is_derived_metric || false}
                                onChange={(_, checked) => updateMapping(col.name, 'is_derived_metric', checked)}
                            />
                        </Stack>
                        
                        {/* KPI & Calculation Fields - Show when is_derived_metric is true */}
                        {mapping.is_derived_metric && (
                            <Stack tokens={{ childrenGap: 8 }} styles={{ root: { 
                                padding: '12px',
                                backgroundColor: '#f0f7ff',
                                borderRadius: '4px',
                                border: '1px solid #cce4ff'
                            } }}>
                                <Label style={{ color: '#0066cc', fontWeight: 600 }}>📐 KPI / Derived Metric Configuration</Label>
                                <TextField
                                    label="KPI Formula"
                                    value={mapping.kpi_formula || ''}
                                    onChange={(_, val) => updateMapping(col.name, 'kpi_formula', val || '')}
                                    placeholder="e.g., profit = revenue - cost; margin = profit / revenue * 100"
                                    description="Formula showing how this metric is calculated from other columns"
                                />
                                <TagInput
                                    label="KPI Dependencies (columns used in formula)"
                                    value={mapping.kpi_dependencies || []}
                                    onChange={(tags) => updateMapping(col.name, 'kpi_dependencies', tags)}
                                    placeholder="Add column name..."
                                />
                                <TextField
                                    label="Calculation Notes"
                                    multiline
                                    rows={2}
                                    value={mapping.calculation_notes || ''}
                                    onChange={(_, val) => updateMapping(col.name, 'calculation_notes', val || '')}
                                    placeholder="Business rules: e.g., 'Excludes canceled orders', 'Use fiscal year for YoY'"
                                />
                            </Stack>
                        )}
                        
                        {/* Filter & Null Handling */}
                        <Stack horizontal tokens={{ childrenGap: 16 }}>
                            <TagInput
                                label="Filter Suggestions (common values)"
                                value={mapping.filter_suggestions || []}
                                onChange={(tags) => updateMapping(col.name, 'filter_suggestions', tags)}
                                placeholder="e.g., USA, Europe, APAC"
                            />
                            <Dropdown
                                label="Null Handling"
                                selectedKey={mapping.null_handling || ''}
                                options={[
                                    { key: '', text: 'Default' },
                                    { key: 'skip', text: 'Skip nulls' },
                                    { key: 'zero', text: 'Treat as 0' },
                                    { key: 'average', text: 'Use average' },
                                    { key: 'carry_forward', text: 'Carry forward' }
                                ]}
                                onChange={(_, option) => updateMapping(col.name, 'null_handling', option?.key as string)}
                                styles={{ root: { minWidth: '150px' } }}
                            />
                        </Stack>
                        
                        <TagInput
                            label="Synonyms (alternative names users might use)"
                            value={mapping.synonyms}
                            onChange={(tags) => updateMapping(col.name, 'synonyms', tags)}
                            placeholder="Add synonym..."
                        />
                        
                        {mapping.synonyms_he && mapping.synonyms_he.length > 0 && (
                            <Stack>
                                <Label>Hebrew Synonyms</Label>
                                <div className={styles.sampleValues}>
                                    {mapping.synonyms_he.map((val, idx) => (
                                        <span key={idx} className={styles.sampleValue}>{val}</span>
                                    ))}
                                </div>
                            </Stack>
                        )}
                        
                        {mapping.sample_values && mapping.sample_values.length > 0 && (
                            <Stack>
                                <Label>Sample Values</Label>
                                <div className={styles.sampleValues}>
                                    {mapping.sample_values.slice(0, 10).map((val, idx) => (
                                        <span key={idx} className={styles.sampleValue}>{val}</span>
                                    ))}
                                </div>
                            </Stack>
                        )}
                        
                        {mapping.confidence !== undefined && mapping.confidence > 0 && (
                            <Text variant="small" style={{ color: '#666' }}>
                                AI Confidence: {(mapping.confidence * 100).toFixed(0)}%
                            </Text>
                        )}
                    </Stack>
                )}
            </div>
        );
    };
    
    if (loading && tables.length === 0) {
        return (
            <Stack horizontalAlign="center" styles={{ root: { padding: '40px' } }}>
                <Spinner size={SpinnerSize.large} label="Loading tables..." />
            </Stack>
        );
    }
    
    return (
        <Stack tokens={stackTokens} styles={{ root: { padding: '20px' } }}>
            {/* Messages */}
            {error && (
                <MessageBar 
                    messageBarType={MessageBarType.error}
                    onDismiss={() => setError(null)}
                >
                    {error}
                </MessageBar>
            )}
            
            {success && (
                <MessageBar 
                    messageBarType={MessageBarType.success}
                    onDismiss={() => setSuccess(null)}
                >
                    {success}
                </MessageBar>
            )}
            
            {/* Table/View Selector - Compact List View */}
            <Stack>
                <div className={styles.tableListContainer}>
                    {/* Header with search and filters */}
                    <div className={styles.tableListHeader}>
                        {/* Search */}
                        <TextField
                            placeholder="Search tables/views..."
                            value={tableSearchQuery}
                            onChange={(e, v) => setTableSearchQuery(v || '')}
                            styles={{ 
                                root: { width: '200px' },
                                field: { fontSize: '13px' }
                            }}
                            iconProps={{ iconName: 'Search' }}
                        />
                        
                        {/* Type Filter */}
                        <Stack horizontal tokens={{ childrenGap: 4 }}>
                            <DefaultButton
                                text={`Active (${tableCounts.activeCount})`}
                                onClick={() => setTableTypeFilter('active')}
                                styles={{
                                    root: {
                                        minWidth: 'auto',
                                        padding: '4px 12px',
                                        backgroundColor: tableTypeFilter === 'active' ? '#107c10' : undefined,
                                        color: tableTypeFilter === 'active' ? 'white' : undefined,
                                        borderColor: tableTypeFilter === 'active' ? '#107c10' : undefined
                                    }
                                }}
                            />
                            <DefaultButton
                                text={`Tables (${tableCounts.tables})`}
                                onClick={() => setTableTypeFilter('table')}
                                styles={{
                                    root: {
                                        minWidth: 'auto',
                                        padding: '4px 12px',
                                        backgroundColor: tableTypeFilter === 'table' ? '#1565c0' : undefined,
                                        color: tableTypeFilter === 'table' ? 'white' : undefined,
                                        borderColor: tableTypeFilter === 'table' ? '#1565c0' : undefined
                                    }
                                }}
                            />
                            <DefaultButton
                                text={`Views (${tableCounts.views})`}
                                onClick={() => setTableTypeFilter('view')}
                                styles={{
                                    root: {
                                        minWidth: 'auto',
                                        padding: '4px 12px',
                                        backgroundColor: tableTypeFilter === 'view' ? '#c62828' : undefined,
                                        color: tableTypeFilter === 'view' ? 'white' : undefined,
                                        borderColor: tableTypeFilter === 'view' ? '#c62828' : undefined
                                    }
                                }}
                            />
                            <DefaultButton
                                text={`All (${tables.length})`}
                                onClick={() => setTableTypeFilter('all')}
                                styles={{
                                    root: {
                                        minWidth: 'auto',
                                        padding: '4px 12px',
                                        backgroundColor: tableTypeFilter === 'all' ? '#667eea' : undefined,
                                        color: tableTypeFilter === 'all' ? 'white' : undefined,
                                        borderColor: tableTypeFilter === 'all' ? '#667eea' : undefined
                                    }
                                }}
                            />
                        </Stack>
                        
                        {/* Bulk actions */}
                        <TooltipHost content="Enable all visible items for AI queries">
                            <IconButton
                                iconProps={{ iconName: 'CheckboxComposite' }}
                                title="Enable All"
                                onClick={() => {
                                    filteredTables.forEach(t => {
                                        if (!t.active_for_queries) {
                                            handleToggleTableActive(t.name, false, t.schema);
                                        }
                                    });
                                }}
                            />
                        </TooltipHost>
                        <TooltipHost content="Disable all visible items for AI queries">
                            <IconButton
                                iconProps={{ iconName: 'Checkbox' }}
                                title="Disable All"
                                onClick={() => {
                                    filteredTables.forEach(t => {
                                        if (t.active_for_queries) {
                                            handleToggleTableActive(t.name, true, t.schema);
                                        }
                                    });
                                }}
                            />
                        </TooltipHost>
                        <TooltipHost content={syncing ? 'Syncing schema metadata...' : 'Refresh schema metadata for active tables/views'}>
                            <IconButton
                                iconProps={{ iconName: 'Sync' }}
                                title="Sync Schema"
                                onClick={handleSyncMetadata}
                                disabled={syncing}
                                className={syncing ? 'sync-spinning' : undefined}
                                styles={{
                                    root: {
                                        color: syncing ? '#0078d4' : undefined
                                    }
                                }}
                            />
                        </TooltipHost>
                        {syncing && (
                            <Spinner size={SpinnerSize.small} style={{ marginLeft: '4px' }} />
                        )}
                        {syncStatus && (
                            <Text variant="small" style={{ 
                                color: syncStatus.startsWith('✓') ? '#107c10' : '#0078d4',
                                marginLeft: '8px',
                                fontWeight: 500
                            }}>
                                {syncStatus}
                            </Text>
                        )}
                    </div>
                    
                    {/* List body */}
                    <div className={styles.tableListBody}>
                        {filteredTables.length === 0 ? (
                            <Stack horizontalAlign="center" style={{ padding: '24px', color: '#666' }}>
                                <Text>No tables/views match your search</Text>
                            </Stack>
                        ) : (
                            filteredTables.map(item => (
                                <div
                                    key={item.name}
                                    className={`${styles.tableListRow} ${selectedTable === item.name ? styles.tableListRowSelected : ''} ${!item.active_for_queries ? styles.tableListRowInactive : ''}`}
                                    onClick={() => handleSelectTable(item.name)}
                                >
                                    {/* Type tag */}
                                    <span className={`${styles.tableTypeTag} ${item.table_type === 'view' ? styles.tableTypeView : styles.tableTypeTable}`}>
                                        {item.table_type === 'view' ? 'VIEW' : 'TABLE'}
                                    </span>
                                    
                                    {/* Name */}
                                    <Text style={{ flex: 1, fontWeight: selectedTable === item.name ? 600 : 400 }}>
                                        {item.name}
                                    </Text>
                                    
                                    {/* Status markers */}
                                    <Stack horizontal tokens={{ childrenGap: 4 }} verticalAlign="center" style={{ marginRight: '8px' }}>
                                        <TooltipHost content={item.description ? `Description: ${item.description}` : 'No description (helps AI select correct table)'} directionalHint={DirectionalHint.topCenter}>
                                            <span style={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                padding: '2px 6px',
                                                borderRadius: '4px',
                                                fontSize: '10px',
                                                fontWeight: 500,
                                                backgroundColor: item.description ? '#fff4ce' : '#f3f2f1',
                                                color: item.description ? '#946200' : '#a19f9d'
                                            }}>
                                                <CheckmarkRegular style={{ fontSize: '10px', marginRight: '2px' }} />
                                                D
                                            </span>
                                        </TooltipHost>
                                        <TooltipHost content={item.has_mappings ? 'Mapping done' : 'No mappings'} directionalHint={DirectionalHint.topCenter}>
                                            <span style={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                padding: '2px 6px',
                                                borderRadius: '4px',
                                                fontSize: '10px',
                                                fontWeight: 500,
                                                backgroundColor: item.has_mappings ? '#dff6dd' : '#f3f2f1',
                                                color: item.has_mappings ? '#107c10' : '#a19f9d'
                                            }}>
                                                <CheckmarkRegular style={{ fontSize: '10px', marginRight: '2px' }} />
                                                M
                                            </span>
                                        </TooltipHost>
                                        <TooltipHost content={item.has_example_questions ? 'Examples done' : 'No examples'} directionalHint={DirectionalHint.topCenter}>
                                            <span style={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                padding: '2px 6px',
                                                borderRadius: '4px',
                                                fontSize: '10px',
                                                fontWeight: 500,
                                                backgroundColor: item.has_example_questions ? '#e5f2ff' : '#f3f2f1',
                                                color: item.has_example_questions ? '#0078d4' : '#a19f9d'
                                            }}>
                                                <CheckmarkRegular style={{ fontSize: '10px', marginRight: '2px' }} />
                                                Q
                                            </span>
                                        </TooltipHost>
                                    </Stack>
                                    
                                    {/* Row count */}
                                    {item.row_count !== undefined && (
                                        <Text variant="small" style={{ color: '#666', minWidth: '80px' }}>
                                            {item.row_count?.toLocaleString()} rows
                                        </Text>
                                    )}
                                    
                                    {/* Active toggle */}
                                    <TooltipHost
                                        content={item.active_for_queries ? 'Active for AI - click to disable' : 'Disabled for AI - click to enable'}
                                        directionalHint={DirectionalHint.topCenter}
                                    >
                                        <Toggle
                                            checked={item.active_for_queries}
                                            onChange={(e, checked) => {
                                                e?.stopPropagation();
                                                handleToggleTableActive(item.name, item.active_for_queries, item.schema);
                                            }}
                                            styles={{
                                                root: { marginBottom: 0 },
                                                pill: { 
                                                    width: '32px', 
                                                    height: '16px',
                                                    background: item.active_for_queries ? '#107c10' : '#d1d1d1'
                                                },
                                                thumb: { width: '12px', height: '12px' }
                                            }}
                                        />
                                    </TooltipHost>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            </Stack>
            
            {/* No table selected */}
            {!selectedTable && tables.length > 0 && (
                <Stack horizontalAlign="center" styles={{ root: { padding: '40px', backgroundColor: '#f5f5f5', borderRadius: '8px' } }}>
                    <TableRegular style={{ fontSize: '48px', color: '#ccc', marginBottom: '16px' }} />
                    <Text variant="large" style={{ color: '#666' }}>Select a table to configure semantic mappings</Text>
                </Stack>
            )}
            
            {/* Column Mappings */}
            {selectedTable && (
                <>
                    <Separator />
                    
                    {/* Table Description - helps AI select correct table */}
                    <Stack tokens={{ childrenGap: 4 }} style={{ marginBottom: '16px' }}>
                        <Label>Table Description (helps AI select correct table)</Label>
                        <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
                            <TextField
                                placeholder="Type a short description (e.g., 'product catalog') then click AI Enhance"
                                value={tableDescription}
                                onChange={(_, val) => setTableDescription(val || '')}
                                styles={{ root: { flex: 1, maxWidth: '600px' } }}
                            />
                            <TooltipHost content="Enhance your description with details from table schema and sample data. Works best when you provide a short description first.">
                                <DefaultButton
                                    onClick={handleEnhanceDescription}
                                    disabled={enhancingDescription}
                                    styles={{ 
                                        root: { 
                                            minWidth: 'auto',
                                            height: '32px',
                                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                            border: 'none'
                                        },
                                        rootHovered: {
                                            background: 'linear-gradient(135deg, #5a6fd6 0%, #6a4190 100%)'
                                        },
                                        label: { color: 'white', fontWeight: 600 }
                                    }}
                                >
                                    <SparkleRegular style={{ marginRight: '4px', color: 'white' }} />
                                    {enhancingDescription ? 'Generating...' : 'AI Enhance'}
                                </DefaultButton>
                            </TooltipHost>
                            <PrimaryButton
                                text={savingDescription ? 'Saving...' : 'Save'}
                                onClick={handleSaveDescription}
                                disabled={savingDescription}
                                styles={{ root: { minWidth: 'auto', height: '32px' } }}
                            />
                        </Stack>
                        <Text variant="small" style={{ color: '#666' }}>
                            Enter a brief description, then click AI Enhance to add technical details from schema and sample data.
                        </Text>
                    </Stack>
                    
                    {/* Actions Bar */}
                    <Stack horizontal horizontalAlign="space-between" verticalAlign="center" wrap tokens={{ childrenGap: 16 }}>
                        <Stack horizontal tokens={{ childrenGap: 16 }} verticalAlign="center">
                            {/* Modern AI Analyze Button */}
                            <div className={styles.aiButtonContainer}>
                                <button
                                    className={`${styles.aiButton} ${analyzing ? styles.aiButtonDisabled : ''}`}
                                    onClick={handleAnalyze}
                                    disabled={analyzing}
                                    style={{
                                        background: analyzing 
                                            ? 'linear-gradient(135deg, #8b9dc3 0%, #9b8bb8 100%)'
                                            : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                                    }}
                                >
                                    {/* Shimmer effect when not analyzing */}
                                    {!analyzing && <div className={styles.aiButtonShimmer} />}
                                    
                                    {/* Progress bar when analyzing */}
                                    {analyzing && <div className={styles.aiButtonProgress} />}
                                    
                                    {/* Button content */}
                                    <SparkleRegular 
                                        style={{ 
                                            fontSize: '20px',
                                            animation: analyzing ? 'sparkle 1s ease-in-out infinite' : 'none'
                                        }} 
                                    />
                                    <span>{analyzing ? 'Analyzing...' : 'AI Analyze'}</span>
                                    
                                    {analyzing && (
                                        <div style={{
                                            width: '16px',
                                            height: '16px',
                                            border: '2px solid rgba(255,255,255,0.3)',
                                            borderTopColor: 'white',
                                            borderRadius: '50%',
                                            animation: 'sparkle 1s linear infinite'
                                        }} />
                                    )}
                                </button>
                            </div>
                            
                            {/* Save Button */}
                            <PrimaryButton
                                iconProps={{ iconName: 'Save' }}
                                text="Save Mappings"
                                onClick={handleSave}
                                disabled={saving}
                                styles={{
                                    root: {
                                        borderRadius: '8px',
                                        padding: '10px 20px'
                                    }
                                }}
                            />
                            
                            {/* Verify All Button with Tooltip */}
                            <TooltipHost
                                content={
                                    <Stack tokens={{ childrenGap: 8 }} styles={{ root: { padding: '8px', maxWidth: '300px' } }}>
                                        <Text variant="mediumPlus" style={{ fontWeight: 600 }}>What is Verification?</Text>
                                        <Text variant="small">
                                            Verification marks that an admin has reviewed and approved the AI's semantic mapping suggestions.
                                        </Text>
                                        <Text variant="small" style={{ fontWeight: 600 }}>Why verify?</Text>
                                        <Text variant="small">
                                            • Verified columns won't be overwritten by future AI analysis<br/>
                                            • Signals that the mapping is correct and trusted<br/>
                                            • Helps track review progress
                                        </Text>
                                        <Text variant="small" style={{ fontStyle: 'italic', color: '#666' }}>
                                            Only columns with a Business Name (analyzed) will be verified.
                                        </Text>
                                    </Stack>
                                }
                                directionalHint={DirectionalHint.bottomCenter}
                                styles={{ root: { display: 'inline-block' } }}
                            >
                                <DefaultButton
                                    iconProps={{ iconName: 'CheckList' }}
                                    text="Verify All"
                                    onClick={handleVerifyAll}
                                    disabled={!Object.values(mappings).some(m => m.business_name && !m.admin_verified)}
                                    styles={{
                                        root: {
                                            borderRadius: '8px',
                                            borderColor: '#28a745',
                                            color: '#28a745'
                                        },
                                        rootHovered: {
                                            backgroundColor: '#d4edda',
                                            borderColor: '#28a745'
                                        }
                                    }}
                                />
                            </TooltipHost>
                            
                            {/* Unverify All - only show if there are verified columns */}
                            {Object.values(mappings).some(m => m.admin_verified) && (
                                <DefaultButton
                                    iconProps={{ iconName: 'Cancel' }}
                                    text="Unverify All"
                                    onClick={handleUnverifyAll}
                                    styles={{
                                        root: {
                                            borderRadius: '8px',
                                            borderColor: '#dc3545',
                                            color: '#dc3545'
                                        },
                                        rootHovered: {
                                            backgroundColor: '#f8d7da',
                                            borderColor: '#dc3545'
                                        }
                                    }}
                                />
                            )}
                        </Stack>
                        
                        {/* Search Input */}
                        <div 
                            className={styles.searchContainer}
                            style={{
                                borderColor: searchQuery ? '#667eea' : '#e1e1e1',
                                boxShadow: searchQuery ? '0 0 0 2px rgba(102, 126, 234, 0.2)' : 'none'
                            }}
                        >
                            <SearchRegular style={{ color: '#666', fontSize: '18px' }} />
                            <input
                                type="text"
                                className={styles.searchInput}
                                placeholder="Search columns by name, business name, or synonyms..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                            {searchQuery && (
                                <DismissRegular 
                                    className={styles.searchClear}
                                    style={{ fontSize: '16px', cursor: 'pointer' }}
                                    onClick={() => setSearchQuery('')}
                                />
                            )}
                        </div>
                    </Stack>
                    
                    {/* Search results indicator */}
                    {searchQuery && (
                        <Text variant="small" style={{ color: '#666', marginTop: '8px' }}>
                            Found {sortedColumns.length} of {columns.length} columns matching "{searchQuery}"
                        </Text>
                    )}
                    
                    <Separator />
                    
                    {/* Analysis Summary Panel - Shows after AI analysis or when there's data */}
                    {(hasRunAnalysis || Object.values(mappings).some(m => m.business_name)) && (
                        <div className={styles.summaryPanel}>
                            <Stack tokens={{ childrenGap: 12 }}>
                                <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
                                    <Text variant="mediumPlus" style={{ fontWeight: FontWeights.semibold }}>
                                        <BrainCircuitRegular style={{ marginRight: '8px' }} />
                                        Analysis Summary
                                    </Text>
                                    <Text variant="small" style={{ color: '#666' }}>
                                        Click status to filter • Sorted by priority
                                    </Text>
                                </Stack>
                                
                                {/* Status Counts */}
                                <Stack horizontal tokens={{ childrenGap: 16 }}>
                                    <div 
                                        className={`${styles.statusCount} ${styles.statusCountMissing}`}
                                        onClick={() => setStatusFilter(statusFilter === 'missing' ? 'all' : 'missing')}
                                        style={{ 
                                            opacity: statusFilter === 'all' || statusFilter === 'missing' ? 1 : 0.5,
                                            transform: statusFilter === 'missing' ? 'scale(1.05)' : 'scale(1)'
                                        }}
                                    >
                                        <ErrorCircleRegular style={{ color: '#dc3545', fontSize: '20px' }} />
                                        <Stack>
                                            <Text variant="large" style={{ fontWeight: FontWeights.bold, color: '#721c24' }}>
                                                {statusSummary.missing}
                                            </Text>
                                            <Text variant="small" style={{ color: '#721c24' }}>Missing</Text>
                                        </Stack>
                                    </div>
                                    
                                    <div 
                                        className={`${styles.statusCount} ${styles.statusCountNeedsReview}`}
                                        onClick={() => setStatusFilter(statusFilter === 'needs-review' ? 'all' : 'needs-review')}
                                        style={{ 
                                            opacity: statusFilter === 'all' || statusFilter === 'needs-review' ? 1 : 0.5,
                                            transform: statusFilter === 'needs-review' ? 'scale(1.05)' : 'scale(1)'
                                        }}
                                    >
                                        <WarningRegular style={{ color: '#ffc107', fontSize: '20px' }} />
                                        <Stack>
                                            <Text variant="large" style={{ fontWeight: FontWeights.bold, color: '#856404' }}>
                                                {statusSummary['needs-review']}
                                            </Text>
                                            <Text variant="small" style={{ color: '#856404' }}>Needs Review</Text>
                                        </Stack>
                                    </div>
                                    
                                    <div 
                                        className={`${styles.statusCount} ${styles.statusCountComplete}`}
                                        onClick={() => setStatusFilter(statusFilter === 'complete' ? 'all' : 'complete')}
                                        style={{ 
                                            opacity: statusFilter === 'all' || statusFilter === 'complete' ? 1 : 0.5,
                                            transform: statusFilter === 'complete' ? 'scale(1.05)' : 'scale(1)'
                                        }}
                                    >
                                        <CheckmarkCircleRegular style={{ color: '#28a745', fontSize: '20px' }} />
                                        <Stack>
                                            <Text variant="large" style={{ fontWeight: FontWeights.bold, color: '#155724' }}>
                                                {statusSummary.complete}
                                            </Text>
                                            <Text variant="small" style={{ color: '#155724' }}>Complete</Text>
                                        </Stack>
                                    </div>
                                    
                                    {statusFilter !== 'all' && (
                                        <DefaultButton
                                            text="Show All"
                                            iconProps={{ iconName: 'ClearFilter' }}
                                            onClick={() => setStatusFilter('all')}
                                            styles={{ root: { marginLeft: 'auto' } }}
                                        />
                                    )}
                                </Stack>
                                
                                {/* Overall Progress */}
                                <Stack tokens={{ childrenGap: 4 }}>
                                    <Stack horizontal horizontalAlign="space-between">
                                        <Text variant="small">Overall Completeness</Text>
                                        <Text variant="small" style={{ fontWeight: FontWeights.semibold }}>
                                            {Math.round((statusSummary.complete / statusSummary.total) * 100)}%
                                        </Text>
                                    </Stack>
                                    <ProgressIndicator 
                                        percentComplete={statusSummary.complete / statusSummary.total}
                                        styles={{
                                            progressBar: {
                                                backgroundColor: statusSummary.complete === statusSummary.total ? '#28a745' : '#667eea'
                                            }
                                        }}
                                    />
                                </Stack>
                                
                                {/* Focus Alert */}
                                {(statusSummary.missing > 0 || statusSummary['needs-review'] > 0) && (
                                    <div className={styles.focusAlert}>
                                        <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
                                            <WarningRegular style={{ color: '#856404', fontSize: '20px' }} />
                                            <Text style={{ color: '#856404' }}>
                                                <strong>Focus on {statusSummary.missing + statusSummary['needs-review']} columns</strong> that need attention. 
                                                {statusSummary.missing > 0 && ` ${statusSummary.missing} need AI analysis.`}
                                                {statusSummary['needs-review'] > 0 && ` ${statusSummary['needs-review']} need review.`}
                                            </Text>
                                        </Stack>
                                    </div>
                                )}
                            </Stack>
                        </div>
                    )}
                    
                    {/* Column Cards */}
                    {loading ? (
                        <Spinner size={SpinnerSize.large} label="Loading columns..." />
                    ) : (
                        <Stack>
                            <Stack horizontal horizontalAlign="space-between" verticalAlign="center" style={{ marginBottom: '12px' }}>
                                <Text variant="mediumPlus" style={{ fontWeight: FontWeights.semibold }}>
                                    Column Mappings 
                                    {statusFilter !== 'all' 
                                        ? ` (${sortedColumns.length} of ${columns.length} - showing ${getStatusLabel(statusFilter)})`
                                        : ` (${columns.length} columns)`
                                    }
                                </Text>
                                {sortedColumns.length > 0 && hasRunAnalysis && (
                                    <Text variant="small" style={{ color: '#666', fontStyle: 'italic' }}>
                                        Sorted by priority: Missing → Needs Review → Complete
                                    </Text>
                                )}
                            </Stack>
                            {sortedColumns.map(renderColumnCard)}
                        </Stack>
                    )}
                </>
            )}
        </Stack>
    );
};

export default SemanticMappingEditor;
